<template>
  <div>
    <app-cells v-if="!leadInfo.reservation_manager">
      <div class="yellow-frame  yellow-frame--m-t">
        <div class="yellow-frame__text">
          Before booking, ensure the Lead's full name is correct. Bookings must be made using the Lead's full name only,
          as it cannot be changed later.
        </div>
      </div>
    </app-cells>
    <app-cells>
      <app-button @click.native="onClickBookHotelButton">Hotel</app-button>
      <app-button @click.native="onClickBookMealButton" theme="lavender">Meal</app-button>
      <app-button v-if="is_general_manager" @click.native="onClickBookFlightButton" theme="info">Flight</app-button>
      <app-button @click.native="onOpenAviasales" theme="info">Flight (Aviasales)</app-button>
      <app-button @click.native="$refs.manual_flight.open()" theme="info">
        Flight (Manual)
      </app-button>
      <app-button @click.native="onClickBookSafariButton" theme="warn" ref="book_safari">
        Safari
      </app-button>
      <app-button
        @click.native="onClickImportHotelsButton"
        ref="import_hotels"
        :disabled="!leadInfo.itinerary"
      >
        Import Hotels
      </app-button>
    </app-cells>
    <app-cells>
      <app-filters
        v-model="active_filter"
        :filters="active_filters"
        name="filterStatus"
        @change="onChangeActiveFilter"
      />
    </app-cells>
    <app-page-container>
      <app-spinner v-if="!is_loaded"/>
      <template v-else>
        <h2 class="title title--mb15">Hotels</h2>
        <template v-if="hotels_reservations.length">
          <AppAccHotel
            v-for="(hotel, hotels_index) in hotels_reservations"
            :key="`hotel_acc_${hotels_index}`"
            :reservation="hotel"
            :order-number="hotels_index + 1"
          />
        </template>
        <app-no-results v-else text="There’s no reservations yet"/>
        <template v-if="meals_reservations.length">
          <h2 class="title title--mb15 title--mt30">Meals</h2>
          <app-cells-grid :columns="1" gap="10px">
            <AppAccMeal
              v-for="(meal, index) in meals_reservations"
              :key="`meal_${meal.id}`"
              :reservation="meal"
              :order-number="index + 1"
            />
          </app-cells-grid>
        </template>
        <template v-if="flight_bookings_loading || flight_bookings_error || flight_bookings.length">
          <h2 class="title title--mb15 title--mt30">Flights</h2>
          <app-spinner v-if="flight_bookings_loading" />
          <app-cells v-else-if="flight_bookings_error">
            <span role="alert">Unable to load flight bookings</span>
            <app-button @click.native="fetchFlightBookings">Retry</app-button>
          </app-cells>
          <app-cells-grid v-else :columns="1" gap="12px">
            <flight-booking-card
              v-for="booking in flight_bookings"
              :key="booking.id"
              :booking="booking"
              :show-lead="false"
              :can-open-booking="canOpenFlightBooking"
            />
          </app-cells-grid>
        </template>
        <template v-if="flights_reservations.length">
          <h2 class="title title--mb15 title--mt30">Flights</h2>
          <AppAccFlight
            v-for="(flight, flights_index) in flights_reservations"
            :key="`flight_${flights_index}`"
            :reservation="flight"
            :order-number="flights_index + 1"
          />
        </template>
        <template v-if="safari_reservations.length">
          <h2 class="title title--mb15 title--mt30">Safari</h2>
          <AppAccSafari
            v-for="(safari, safari_index) in safari_reservations"
            :key="`safari_${safari_index}`"
            :reservation="safari"
            :order-number="safari_index + 1"
          />
        </template>
      </template>
    </app-page-container>
    <manual-flight-booking-modal
      ref="manual_flight"
      :lead-id="Number($route.params.id)"
      :can-create="true"
    />
    <modal
      name="lead-aviasales-modal"
      :width="1100"
      height="auto"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      :reset="true"
      @closed="aviasalesOpen = false"
    >
      <div class="popup lead-aviasales-popup">
        <button @click="$modal.hide('lead-aviasales-modal')" class="popup__close" type="button" aria-label="Close flight search" />
        <div class="popup__title">Flights (Aviasales)</div>
        <div class="popup__body">
          <p v-if="aviasalesInitialSearch.departure">Departure date suggested from the lead's travel dates. Confirm it before searching.</p>
          <p v-if="leadInfo.children || leadInfo.teenagers">Passenger counts are prefilled from the lead. Check the airline's age categories before searching.</p>
          <flight-booking-flow
            v-if="aviasalesOpen"
            :allow-round-trip="false"
            :can-select="true"
            :initial-search="aviasalesInitialSearch"
            :show-filters="false"
            :booking-lead-id="Number($route.params.id)"
            @booking-created="$modal.hide('lead-aviasales-modal')"
          />
        </div>
      </div>
    </modal>
    <modal
      name="hotel-reservation-create-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('hotel-reservation-create-modal')" class="popup__close" type="button" />
        <div class="popup__title">
          <app-cells :indent="false" position="center">
            <span>Create Hotel Reservation</span>
            <app-checkbox
              v-if="!form_hotel_reservation.hotel?.property"
              v-model="form_hotel_reservation.already_booked"
              label="Already booked"
            />
          </app-cells>
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckCreateHotelForm">
            <app-flex-grid :rows="[[1, 1], [1, 1, '50px', 1], ['60px', 1, '60px', 1], ['200px'], [1], [1], [1]]">
              <template #col-1-1>
                <app-form-field label="Hotel" required>
                  <app-select
                    @input="onChangeSelectHotelReservation"
                    v-model="form_hotel_reservation.hotel"
                    :options="hotels_options"
                    :clearable="false"
                    placeholder="Choose hotel"
                    :error="$v.form_hotel_reservation.hotel.$error"
                    :readonly="hotel_short_rooms_checked.length > 0"
                  />
                  <template #error>
                    <div v-if="$v.form_hotel_reservation.hotel.$dirty && !$v.form_hotel_reservation.hotel.required">Required field</div>
                  </template>
                </app-form-field>
              </template>
              <template #col-1-2>
                <app-form-field v-if="!form_hotel_reservation.hotel?.property" label="Status" required>
                  <app-select
                    v-model="form_hotel_reservation.sales_status"
                    :reduce="item => item.code"
                    :options="reservations_sales_statuses"
                    :clearable="false"
                    placeholder="Status"
                    :error="$v.form_hotel_reservation.sales_status.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_hotel_reservation.sales_status.$dirty && !$v.form_hotel_reservation.sales_status.required">
                      Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #header-2>
                <h3 class="title title--mb15">Room configuration</h3>
              </template>
              <template #col-2-1>
                <app-form-field label="Check-in">
                  <app-date-picker
                    @input="$refs.check_out_hotels.$children[0].focus()"
                    v-model="form_hotel_reservation.check_in"
                    placeholder="Check-in"
                    :readonly="!form_hotel_reservation.hotel"
                    :clearable="false"
                  />
                </app-form-field>
              </template>
              <template #col-2-2>
                <app-form-field label="Check-out">
                  <app-date-picker
                    v-model="form_hotel_reservation.check_out"
                    placeholder="Check-out"
                    :readonly="!form_hotel_reservation.check_in"
                    :default-value="form_hotel_reservation.check_in"
                    ref="check_out_hotels"
                    :clearable="false"
                  />
                </app-form-field>
              </template>
              <template #col-2-3>
                <app-form-field label="Nights" disabled>
                  <app-input :value="calcNumberOfNights" disabled />
                </app-form-field>
              </template>
              <template #col-2-4>
                <app-form-field label="Meal plan">
                  <app-select
                    v-model="form_hotel_reservation.meal_plan"
                    :options="meal_plans_options"
                    :clearable="false"
                    placeholder="Meal plan"
                    :readonly="!form_hotel_reservation.check_out"
                  />
                </app-form-field>
              </template>
              <template #col-3-1>
                <app-checkbox
                  v-if="!form_hotel_reservation.hotel?.property"
                  v-model="form_hotel_reservation.early_check_in"
                  label="Early"
                  :readonly="!form_hotel_reservation.hotel"
                />
              </template>
              <template #col-3-2>
                <app-form-field v-if="!form_hotel_reservation.hotel?.property" label="Check-in time" label-for="early_check_in_time" label-left>
                  <app-input
                    v-model="form_hotel_reservation.early_check_in_time"
                    v-mask="'99:99'"
                    placeholder="--:--"
                    autocomplete="off"
                    id="early_check_in_time"
                    name="early_check_in_time"
                    @paste.native.prevent
                    :disabled="!form_hotel_reservation.early_check_in"
                    class="input--65"
                  />
                </app-form-field>
              </template>
              <template #col-3-3>
                <app-checkbox
                  v-if="!form_hotel_reservation.hotel?.property"
                  v-model="form_hotel_reservation.late_check_out"
                  label="Late"
                  :readonly="!form_hotel_reservation.hotel"
                />
              </template>
              <template #col-3-4>
                <app-form-field v-if="!form_hotel_reservation.hotel?.property" label="Check-out time" label-for="late_check_out_time" label-left>
                  <app-input
                    v-model="form_hotel_reservation.late_check_out_time"
                    v-mask="'99:99'"
                    placeholder="--:--"
                    autocomplete="off"
                    id="late_check_out_time"
                    name="late_check_out_time"
                    @paste.native.prevent
                    :disabled="!form_hotel_reservation.late_check_out"
                    class="input--65"
                  />
                </app-form-field>
              </template>
              <template #col-4-1>
                <app-form-field label="Room">
                  <app-select
                    v-model="form_hotel_reservation.type_of_rooms"
                    :options="rooms_options"
                    :clearable="false"
                    placeholder="Choose rooms"
                    :readonly="!form_hotel_reservation.meal_plan"
                  />
                </app-form-field>
              </template>
              <template #col-5-1>
                <app-cells
                  v-if="form_hotel_reservation.type_of_rooms &&
                    form_hotel_reservation.type_of_rooms.accomodation_types &&
                    form_hotel_reservation.type_of_rooms.accomodation_types.length"
                  :indent="false"
                  :gap="'10px'"
                >
                  <app-room-icon
                    @click="onRoomIconClick(type)"
                    v-for="(type, index) in form_hotel_reservation.type_of_rooms.accomodation_types"
                    :key="index"
                    :accomodation_type="type"
                    :disabled="checkExistsInArray(hotel_short_rooms_checked, convertToRoom(type))"
                    :class="{ 'room-icon__disabled': checkExistsInArray(hotel_short_rooms_checked, convertToRoom(type)) }"
                  />
                </app-cells>
              </template>
              <template #header-6>
                <h3 class="title title--mb15">Rooms</h3>
              </template>
              <template #col-6-1>
                <template v-if="hotel_short_rooms_checked.length > 0">
                  <AppHotelCard
                    v-for="(room, index) in hotel_short_rooms_checked"
                    :key="index"
                    @onCardDelete="onShortRoomDelete(index, 'hotel_short_rooms_checked')"
                    @onCountInput="onCountChange($event, index, 'hotel_short_rooms_checked')"
                    @changeSomeField="onChangeSomeField($event, index, 'hotel_short_rooms_checked')"
                    :order_number="index + 1"
                    :name="room.type_of_rooms.name"
                    :accomodation_type="room.accomodation_type"
                    :check_in="room.check_in"
                    :check_out="room.check_out"
                    :mp="room.meal_plan.name"
                    :early="room.early_check_in_time || null"
                    :late="room.late_check_out_time || null"
                    :count="1"
                    :bed-in-twin="room.type_of_rooms.bed_in_twin"
                    :property="form_hotel_reservation.hotel.property"
                  />
                </template>
                <app-no-results v-else text="There’s no rooms yet" small />
              </template>
              <template #col-7-1>
                <app-form-field label="Sales manager notes for the hotel" label-for="notes">
                  <app-textarea
                    v-model="form_hotel_reservation.notes"
                    rows="5"
                    placeholder="Write a note"
                    id="notes"
                  />
                </app-form-field>
              </template>
            </app-flex-grid>
            <app-cells position="center" :indent="false">
              <app-button
                :disabled="$v.form_hotel_reservation.$error || !hotel_short_rooms_checked.length"
                type="submit"
                ref="submit_hotel_create"
              >
                Create
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="import-hotels-modal"
      :width="600"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('import-hotels-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Import Story Hotels</div>
        <div class="popup__body">
          <div v-for="(hotel_item, index) in imported_hotels" :key="`hotel_item_${index}`" class="import-hotel">
            <app-flex-grid :rows="[['300px', 1], ['250px'], [1], [1]]" :margin-bottom="0">
              <template #col-1-1>
                <app-form-field label="Hotel">
                  <app-input :value="hotel_item.hotel.name" class="input--min-300" readonly/>
                </app-form-field>
              </template>
              <template #col-1-2>
                <app-form-field label="Import hotel">
                  <app-switcher v-model="hotel_item.checked_import"/>
                </app-form-field>
              </template>
              <template #col-2-1>
                <app-form-field v-if="hotel_item.checked_import && !hotel_item.hotel?.property" label="Status" required>
                  <app-select
                    v-model="hotel_item.status"
                    :reduce="item => item.code"
                    :options="reservations_sales_statuses"
                    :clearable="false"
                    placeholder="Status"
                    :error="$v.imported_hotels.$each[index].status.$error"
                  />
                  <template #error>
                    <div
                      v-if="$v.imported_hotels.$each[index].status.$dirty && !$v.imported_hotels.$each[index].status.required">
                      Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-3-1>
                <app-form-field
                  v-if="hotel_item.checked_import"
                  label="Sales manager note for the hotel"
                  label-for="checked_import_notes"
                >
                  <app-textarea
                    v-model="hotel_item.notes"
                    id="checked_import_notes"
                    placeholder="Sales manager note for the hotel"
                  />
                </app-form-field>
              </template>
              <template #col-4-1>
                <AppHotelCard
                  v-for="(room, room_index) in hotel_item.rooms"
                  :key="`hotel_room_${room.hotel_room.id}_${room_index}`"
                  @changeSomeField="onChangeSomeFieldImport($event, room_index, index)"
                  :order_number="room_index + 1"
                  :name="room.hotel_room.name"
                  :accomodation_type="room.accomodation_type"
                  :check_in="room.check_in"
                  :check_out="room.check_out"
                  :mp="room.meal_plan.name"
                  :early="room.early_check_in_time || null"
                  :late="room.late_check_out_time || null"
                  :count="room.count"
                  :clearable="false"
                  :bed-in-twin="room.hotel_room.bed_in_twin"
                  :property="hotel_item.hotel.property"
                />
              </template>
            </app-flex-grid>
          </div>
        </div>
        <app-cells position="center" :indent="false">
          <app-button @click.native="onCheckImportHotelForm" ref="send_import_hotels"
                      :disabled="$v.imported_hotels.$error">
            Import
          </app-button>
        </app-cells>
      </div>
    </modal>
    <modal
      name="flight-reservation-create-modal"
      :width="800"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('flight-reservation-create-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">
          <app-cells :indent="false" position="center">
            <span>Create Flight Reservation</span>
            <app-checkbox
              v-model="form_flight_reservation.already_booked"
              label="Already booked"
            />
          </app-cells>
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckCreateFlightForm">
            <app-flex-grid :rows="[[1], [1, 1]]">
              <template #col-1-1>
                <app-form-field label="Search schedule" label-left>
                  <app-switcher
                    @change="onChangeSwitcherFlightSearch(switcher_flight_search)"
                    v-model="switcher_flight_search"
                  />
                </app-form-field>
              </template>
              <template #col-2-1>
                <app-form-field label="Date" required>
                  <app-date-picker
                    @input="onDateChanged"
                    v-model="form_flight_reservation.date"
                    placeholder="Date"
                    :error="$v.form_flight_reservation.date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_flight_reservation.date.$dirty && !$v.form_flight_reservation.date.required">
                      Required field
                    </div>
                    <div v-if="$v.form_flight_reservation.date.$dirty && !$v.form_flight_reservation.date.minValue">
                      Date cannot be less than the current date
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-2>
                <app-form-field label="Status" required>
                  <app-select
                    v-model="form_flight_reservation.sales_status"
                    :reduce="item => item.code"
                    :options="reservations_sales_statuses_flights"
                    :clearable="false"
                    placeholder="Status"
                    :error="$v.form_flight_reservation.sales_status.$error"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_flight_reservation.sales_status.$dirty && !$v.form_flight_reservation.sales_status.required">
                      Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
            </app-flex-grid>
            <app-flex-grid v-if="switcher_flight_search" :rows="[[1, 1], ['250px', '160px', '70px']]">
              <template #header-1>
                <h3 class="title title--mb15">Flight configuration</h3>
              </template>
              <template #col-1-1>
                <app-form-group label="Origin" class="form-group--no-indent" required>
                  <app-select
                    @input="onOriginChange"
                    v-model="form_flight_reservation.flight_from"
                    :reduce="item => item.id"
                    :options="flight_from_options"
                    :clearable="false"
                    placeholder="Choose origin"
                    :error="$v.form_flight_reservation.flight_from.$error"
                    :readonly="!form_flight_reservation.date"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_flight_reservation.flight_from.$dirty && !$v.form_flight_reservation.flight_from.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-1-2>
                <app-form-group label="Destination" class="form-group--no-indent" required>
                  <app-select
                    @input="onDestinationChange"
                    v-model="form_flight_reservation.flight_to"
                    :reduce="item => item.id"
                    :options="flight_to_options"
                    :clearable="false"
                    placeholder="Choose destination"
                    :error="$v.form_flight_reservation.flight_to.$error"
                    :readonly="!form_flight_reservation.flight_from"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_flight_reservation.flight_to.$dirty && !$v.form_flight_reservation.flight_to.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-1>
                <app-form-group label="Airline" class="form-group--no-indent" required>
                  <app-select
                    @input="onAirlineChange"
                    v-model="form_flight_reservation.airline"
                    :options="airline_options"
                    :clearable="false"
                    placeholder="Choose airline"
                    :error="$v.form_flight_reservation.airline.$error"
                    :readonly="!form_flight_reservation.flight_to"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_flight_reservation.airline.$dirty && !$v.form_flight_reservation.airline.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-2>
                <app-form-group label="Departure" class="form-group--no-indent" required>
                  <app-select
                    @input="onDepartureScheduleChange"
                    v-model="form_flight_reservation.departure"
                    :options="schedule_options"
                    :clearable="false"
                    label="departure"
                    placeholder="Departure"
                    :error="$v.form_flight_reservation.departure.$error"
                    :readonly="!schedule_options.length"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_flight_reservation.departure.$dirty && !$v.form_flight_reservation.departure.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-3>
                <app-form-group v-if="form_flight_reservation.departure" class="form-group--no-indent" label="Arrival">
                  <app-input v-model="form_flight_reservation.arrival" readonly/>
                </app-form-group>
              </template>
            </app-flex-grid>
            <app-flex-grid v-else :rows="[[1, 1, 1], ['70px', '70px']]">
              <template #header-1>
                <h3 class="title title--mb15">Flight configuration</h3>
              </template>
              <template #col-1-1>
                <app-form-group label="Airline" class="form-group--no-indent" required>
                  <app-select
                    v-model="form_flight_reservation.airline"
                    :options="airline_alt_options"
                    :clearable="false"
                    placeholder="Choose airline"
                    :error="$v.form_flight_reservation.airline.$error"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_flight_reservation.airline.$dirty && !$v.form_flight_reservation.airline.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-1-2>
                <app-form-group label="Origin" class="form-group--no-indent" required>
                  <app-select
                    v-model="form_flight_reservation.flight_from"
                    :reduce="item => item.id"
                    :options="form_flight_reservation?.airline?.airstrips"
                    :clearable="false"
                    placeholder="Choose origin"
                    :error="$v.form_flight_reservation.flight_from.$error"
                    :readonly="!form_flight_reservation.airline"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_flight_reservation.flight_from.$dirty && !$v.form_flight_reservation.flight_from.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-1-3>
                <app-form-group label="Destination" class="form-group--no-indent" required>
                  <app-select
                    v-model="form_flight_reservation.flight_to"
                    :reduce="item => item.id"
                    :options="form_flight_reservation?.airline?.airstrips"
                    :clearable="false"
                    placeholder="Choose destination"
                    :error="$v.form_flight_reservation.flight_to.$error"
                    :readonly="!form_flight_reservation.flight_from"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_flight_reservation.flight_to.$dirty && !$v.form_flight_reservation.flight_to.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-1>
                <app-form-group label="Departure" class="form-group--no-indent" required>
                  <app-input
                    v-model="form_flight_reservation.departure"
                    v-mask="'99:99'"
                    placeholder="00:00"
                    autocomplete="off"
                    class="input--100"
                    id="time"
                    name="time"
                    @paste.native.prevent
                    :error="$v.form_flight_reservation.departure.$error"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_flight_reservation.departure.$dirty && !$v.form_flight_reservation.departure.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-2>
                <app-form-group label="Arrival" class="form-group--no-indent" required>
                  <app-input
                    v-model="form_flight_reservation.arrival"
                    v-mask="'99:99'"
                    placeholder="00:00"
                    autocomplete="off"
                    class="input--100"
                    id="time"
                    name="time"
                    @paste.native.prevent
                    :error="$v.form_flight_reservation.arrival.$error"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_flight_reservation.arrival.$dirty && !$v.form_flight_reservation.arrival.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
            </app-flex-grid>
            <h3
              v-if="(switcher_flight_search && form_flight_reservation.departure) || (!switcher_flight_search && form_flight_reservation.airline)"
              class="title title--mb15"
            >
              Luggage options
            </h3>
            <app-cells v-if="switcher_flight_search && form_flight_reservation.departure" :indent="false"
                       :margin-bottom="20">
              <app-luggage-card
                @clickCard="onLuggageClick(card)"
                v-for="card in form_flight_reservation.departure.airline.luggage_options"
                :key="`card_${card.id}`"
                :age_type="card.age_type"
                :weight="card.weight"
                :disable="checkExistsInArray(seats_options, card)"
              />
            </app-cells>
            <app-cells v-if="!switcher_flight_search && form_flight_reservation.airline" :indent="false"
                       :margin-bottom="20">
              <app-luggage-card
                @clickCard="onLuggageClick(card)"
                v-for="card in form_flight_reservation.airline.luggage_options"
                :key="`card_${card.id}`"
                :age_type="card.age_type"
                :weight="card.weight"
                :disable="checkExistsInArray(seats_options, card)"
              />
            </app-cells>
            <h3 class="title title--mb15">Seats</h3>
            <app-cells-grid v-if="seats_options.length" :columns="2" gap="10px 20px" :margin-bottom="20">
              <app-lug-item
                v-for="(seat, seat_index) in seats_options"
                :key="`lug_${seat_index}`"
                @onDeleteLugItem="onShortRoomDelete(seat_index, 'seats_options')"
                @onCountInput="onCountChange($event, seat_index, 'seats_options')"
                :order-number="seat_index + 1"
                :age-type="seat.age_type"
                :weight="seat.weight"
                :count="1"
                seats-assignment
              />
            </app-cells-grid>
            <app-no-results v-else text="There’s no seats yet" small :margin-bottom="20"/>
            <app-form-field label="Sales manager note for the airline" label-for="flight_reservation_notes"
                            :margin-bottom="20">
              <app-textarea
                v-model="form_flight_reservation.notes"
                rows="5"
                placeholder="Write a note"
                id="flight_reservation_notes"
              />
            </app-form-field>
            <app-cells position="center" :indent="false">
              <app-button
                type="submit"
                ref="submit_flight_create"
                :disabled="$v.form_flight_reservation.$error || !seats_options.length"
              >
                Create
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="safari-reservation-create-modal"
      :width="520"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('safari-reservation-create-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Create Safari Reservation</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckCreateSafariForm">
            <app-flex-grid :rows="[[1, '90px', '100px' ], [1, 1], [1, 1], [1, 1], [1]]" align-items="flex-end">
              <template #col-1-1>
                <app-form-field label="Group Type" required>
                  <app-select
                    v-model="form_safari_reservation.group_type"
                    :reduce="item => item.code"
                    :options="group_type_options"
                    :clearable="false"
                    placeholder="Group type"
                    :error="$v.form_safari_reservation.group_type.$error"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_safari_reservation.group_type.$dirty && !$v.form_safari_reservation.group_type.required">
                      Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-1-2>
                <app-form-field label="Pax" label-for="pax" required>
                  <app-input
                    v-model.number="form_safari_reservation.pax"
                    id="pax"
                    name="pax"
                    type="number"
                  />
                  <template #error>
                    <div v-if="$v.form_safari_reservation.pax.$dirty && !$v.form_safari_reservation.pax.required">
                      Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-1-3>
                <app-form-field label="Cars required" label-for="cars_required" required>
                  <app-input
                    v-model.number="form_safari_reservation.cars_required"
                    @change.native="$v.form_safari_reservation.cars_required.$touch()"
                    :error="$v.form_safari_reservation.cars_required.$error"
                    id="cars_required"
                    name="cars_required"
                    type="number"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_safari_reservation.cars_required.$dirty && !$v.form_safari_reservation.cars_required.required">
                      Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-1>
                <app-form-field label="Start from" required>
                  <app-select
                    @input="onLocationStartSelected($event, 'form_safari_reservation')"
                    v-model="form_safari_reservation.start_from"
                    :options="location_start_options"
                    :clearable="false"
                    @search="onLocationStartSearch"
                    placeholder="Start searching"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_safari_reservation.start_from.$dirty && !$v.form_safari_reservation.start_from.required">
                      Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-2>
                <app-form-field label="Finish at" required>
                  <app-select
                    @input="onLocationFinishSelected($event, 'form_safari_reservation')"
                    v-model="form_safari_reservation.finish_at"
                    :options="location_finish_options"
                    :clearable="false"
                    @search="onLocationFinishSearch"
                    placeholder="Start searching"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_safari_reservation.finish_at.$dirty && !$v.form_safari_reservation.finish_at.required">
                      Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #header-3>
                <h3 class="title title--mb15">Itinerary configuration</h3>
              </template>
              <template #col-3-1>
                <app-form-field label="Date">
                  <app-date-picker
                    v-model="itinerary_safari_config.date"
                    placeholder="Date"
                    :clearable="false"
                  />
                </app-form-field>
              </template>
              <template #col-3-2>
                <app-form-field label="Type">
                  <app-select
                    @input="onItineraryTypeSelected"
                    v-model="itinerary_safari_config.type"
                    :options="itinerary_types_options"
                    :clearable="false"
                    placeholder="Type"
                    :error="$v.form_safari_reservation.group_type.$error"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_safari_reservation.group_type.$dirty && !$v.form_safari_reservation.group_type.required">
                      Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-4-1 v-if="itinerary_safari_config.type?.code !== 'rest_day'">
                <app-form-field label="National park">
                  <app-select
                    @input="onNationalParkSelected"
                    v-model="itinerary_safari_config.national_park"
                    :options="national_park_options"
                    :clearable="false"
                    placeholder="National park"
                  />
                </app-form-field>
              </template>
              <template #col-4-2 v-if="itinerary_safari_config.type?.code !== 'rest_day'">
                <app-form-field label="Area">
                  <app-select
                    v-model="itinerary_safari_config.national_park_area"
                    :options="area_options"
                    :clearable="false"
                    placeholder="Area"
                    :readonly="!itinerary_safari_config.national_park || !itinerary_safari_config.national_park.area.length"
                  />
                </app-form-field>
              </template>
              <template #col-5-1>
                <app-cells :indent="false" position="center">
                  <app-button
                    @click.native="onAddItinerary"
                    :disabled="!itinerary_safari_config.date || !itinerary_safari_config.type"
                  >
                    Add
                  </app-button>
                </app-cells>
              </template>
            </app-flex-grid>
            <app-form-group label="Spoken language">
              <app-select
                v-model="form_safari_reservation.spoken_language"
                :options="language_options"
                :reduce="language => language.code"
                label="name"
                placeholder="Select group language"
              />
            </app-form-group>
            <app-cells :indent="false" gap="15px" margin-bottom="20px">
              <app-checkbox v-model="form_safari_reservation.honeymooners" label="Honeymooners"/>
              <app-checkbox v-model="form_safari_reservation.birdwatchers" label="Birdwatchers"/>
              <app-checkbox v-model="form_safari_reservation.special_assistance" label="Special assistance"/>
            </app-cells>
            <app-cells :indent="false" margin-bottom="15px">
              <h3 class="title">Itinerary</h3>
              <app-button @click.native="onClickImportSafari" size="small" ref="import_safari">Import</app-button>
            </app-cells>
            <template v-if="itinerary_safari_list.length">
              <SafariItineraryDayList
                :items="itinerary_safari_list"
                :validation-error="safari_itinerary_error"
                @delete="onDeleteCreateSafariItinerary"
                @add-missing="onSelectMissingSafariDay"
              />
            </template>
            <app-no-results v-else text="There’s no itinerary yet" small :margin-bottom="20"/>
            <app-form-group label="Sales manager notes for safari company" label-for="notes">
              <app-textarea
                v-model="form_safari_reservation.notes"
                id="notes"
                placeholder="Write a notes"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button
                type="submit"
                ref="submit_safari_create"
                :disabled="$v.form_safari_reservation.$error || !!safari_itinerary_error"
              >
                Create
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="request-meal-popup"
      :width="920"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('request-meal-popup')" class="popup__close" type="button"></button>
        <div class="popup__title">
          <app-cells :indent="false" position="center">
            Create Meal Reservation
            <app-checkbox
              v-model="form_request_meal.already_booked"
              label="Already booked"
            />
          </app-cells>
        </div>
        <div class="popup__body">
          <form @submit.prevent="sendRequestMealForm">
            <app-flex-grid
              :rows="[['410px'], [1, '130px', '80px', '80px', '80px', 'auto'], [1], [1]]"
              align-items="flex-end"
            >
              <template #col-1-1>
                <app-form-field label="Hotel" required>
                  <app-select
                    v-model="form_request_meal.hotel"
                    :reduce="item => item.id"
                    :options="hotels_options"
                    :clearable="false"
                    placeholder="Choose hotel"
                    :readonly="form_request_meal.requests.length > 0"
                    :error="$v.form_request_meal.hotel.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.hotel.$dirty && !$v.form_request_meal.hotel.required">Required
                      field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #header-2>
                <h3 class="title title--mb15">Configuration</h3>
              </template>
              <template #col-2-1>
                <app-form-field label="Type" required>
                  <app-select
                    v-model="form_request_meal.type"
                    :options="meal_type_options"
                    :clearable="false"
                    label="name"
                    placeholder="Choose type"
                    :error="$v.form_request_meal.type.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.type.$dirty && !$v.form_request_meal.type.required">Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-2>
                <app-form-field label="Date" required>
                  <app-date-picker
                    v-model="form_request_meal.date"
                    placeholder="Date"
                    :clearable="false"
                    :error="$v.form_request_meal.date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.date.$dirty && !$v.form_request_meal.date.required">
                      Required field
                    </div>
                    <div v-if="$v.form_request_meal.date.$dirty && !$v.form_request_meal.date.minValue">
                      Date cannot be less than the current date
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-3>
                <app-form-field label="Time" required>
                  <app-input
                    v-model="form_request_meal.time"
                    v-mask="'99:99'"
                    placeholder="00:00"
                    autocomplete="off"
                    id="time"
                    name="time"
                    @paste.native.prevent
                    :error="$v.form_request_meal.time.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.time.$dirty && !$v.form_request_meal.time.timeRegex">Wrong time
                    </div>
                    <div v-if="$v.form_request_meal.time.$dirty && !$v.form_request_meal.time.required">Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-4>
                <app-form-field label="Clients" label-for="add_meal_clients" required>
                  <app-input
                    v-model.number="form_request_meal.clients"
                    type="number"
                    name="add_meal_clients"
                    id="add_meal_clients"
                    placeholder=""
                    :error="$v.form_request_meal.clients.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.clients.$dirty && !$v.form_request_meal.clients.required">Required
                      field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-5>
                <app-form-field label="Crew" label-for="add_meal_crew" required>
                  <app-input
                    v-model.number="form_request_meal.crew"
                    type="number"
                    name="add_meal_crew"
                    id="add_meal_crew"
                    placeholder=""
                    :error="$v.form_request_meal.crew.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.crew.$dirty && !$v.form_request_meal.crew.required">Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-6>
                <app-button @click.native="onClickRequestMealConfig" :disabled="$v.form_request_meal.$error">Add
                </app-button>
              </template>
              <template #header-3>
                <h3 class="title title--mb15">Meals</h3>
              </template>
              <template #col-3-1>
                <app-cells-grid v-if="form_request_meal.requests.length > 0" :columns="1" gap="0" :margin-bottom="0">
                  <AppMealCard
                    v-for="(card, index) in form_request_meal.requests"
                    :key="`meal_card_${index}`"
                    @cardDelete="onDeleteMealsCard(index)"
                    :order-number="index + 1"
                    :meal="card"
                  />
                </app-cells-grid>
                <app-no-results v-else text="There’s no meals yet" small/>
              </template>
              <template #col-4-1>
                <app-form-field label="Sales manager notes for the hotel" label-for="request_meal_notes">
                  <app-textarea
                    v-model="form_request_meal.notes"
                    rows="5"
                    placeholder="Write a note"
                    id="request_meal_notes"
                  />
                </app-form-field>
              </template>
            </app-flex-grid>
            <app-cells :indent="false" position="center">
              <app-button
                type="submit"
                ref="request_meal_submit"
                :disabled="form_request_meal.requests.length < 1 || !form_request_meal.hotel"
              >
                Request
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getFlightsBookings,
  getDomesticFlightsAirlines,
  getDomesticFlightsSchedulesAirlinesAvailable,
  getDomesticFlightsSchedulesDestinationAvailable,
  getDomesticFlightsSchedulesOriginAvailable,
  getDomesticFlightsSchedulesScheduleSelection,
  getGoogleSearch,
  getHotelsListSearch,
  getHotelsRooms,
  getReservationsFlights,
  getReservationsHotelsList,
  getReservationsItineraryTypes,
  getReservationsMeals,
  getReservationsMealType,
  getReservationsSafari,
  getReservationsSalesStatuses,
  getLanguages,
  getSafariGroupTypes,
  getSafariNationalPark,
  postLocation,
  postReservationsFlights,
  postReservationsHotelsCreate,
  postReservationsHotelsStoryCreate,
  postReservationsHotelsStoryImport,
  postReservationsMeal,
  postReservationsSafari,
  postReservationsSafariStoryImport
} from '@/http'
import FlightBookingCard from '@/components/flights/FlightBookingCard.vue'
import { flightBookingRoles } from '@/utils/flightBookingAccess'
import FlightBookingFlow from '@/components/flights/FlightBookingFlow.vue'
import ManualFlightBookingModal from '@/components/flights/ManualFlightBookingModal.vue'
import { leadFlightSearch } from './leadFlightSearch'
import AppAccHotel from '@/components/AppAccHotel.vue'
import AppHotelCard from '@/components/AppHotelCard.vue'
import AppRoomIcon from '@/components/AppRoomIcon.vue'
import AppAccFlight from '@/components/AppAccFlight.vue'
import AppAccSafari from '@/components/AppAccSafari.vue'
import AppLuggageCard from '@/components/AppLuggageCard.vue'
import AppLugItem from '@/components/AppLugItem.vue'
import SafariItineraryDayList from '@/components/SafariItineraryDayList.vue'
import { safariItineraryError } from '@/utils/safariItineraryDays'
import debounce from 'lodash/debounce'
import AppAccMeal from '@/components/AppAccMeal.vue'
import { helpers, required, requiredIf } from 'vuelidate/lib/validators'
import AppMealCard from '@/components/AppMealCard.vue'
import { format } from 'date-fns'

const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):([0-5][0-9])?$/i)

export default {
  name: 'LeadsDetailReservations',
  components: {
    FlightBookingCard,
    FlightBookingFlow,
    ManualFlightBookingModal,
    AppMealCard,
    AppAccMeal,
    SafariItineraryDayList,
    AppLugItem,
    AppLuggageCard,
    AppAccSafari,
    AppAccFlight,
    AppRoomIcon,
    AppHotelCard,
    AppAccHotel,
  },
  props: {
    leadInfo: {
      type: Object,
    },
  },
  data() {
    return {
      aviasalesOpen: false,
      aviasalesInitialSearch: {},
      is_loaded: false,
      hotels_reservations: [],
      active_filter: 'active',
      active_filters: [
        { value: 'active', label: 'Active' },
        { value: 'archive', label: 'Archive' },
      ],
      form_hotel_reservation: {},
      hotel_short_rooms_checked: [],
      hotels_options: [],
      rooms_options: [],
      reservations_sales_statuses: [],
      reservations_sales_statuses_flights: [],
      meal_plans_options: [],
      imported_hotels: [],
      flights_reservations: [],
      flight_bookings: [],
      flight_bookings_loading: false,
      flight_bookings_error: false,
      flight_bookings_request_id: 0,
      safari_reservations: [],
      form_flight_reservation: {
        date: null,
        sales_status: null,
        flight_from: null,
        flight_to: null,
        airline: null,
        departure: null,
        arrival: null,
      },
      flight_from_options: [],
      flight_to_options: [],
      airline_options: [],
      airline_alt_options: [],
      schedule_options: [],
      seats_options: [],
      form_safari_reservation: {},
      location_start_options: [],
      location_finish_options: [],
      itinerary_safari_config: {},
      area_options: [],
      national_park_options: [],
      itinerary_safari_list: [],
      switcher_flight_search: true,
      form_request_meal: { requests: [] },
      meal_type_options: [],
      meals_reservations: [],
      group_type_options: [],
      itinerary_types_options: [],
      language_options: [],
    }
  },
  validations: {
    imported_hotels: {
      $each: {
        status: {
          required: requiredIf(function (value) {
            return value.checked_import && !value.hotel.property
          }),
        },
      }
    },
    form_hotel_reservation: {
      hotel: { required },
      sales_status: {
        required: requiredIf(function() {
          return !this.form_hotel_reservation.hotel?.property
        })
      },
    },
    form_flight_reservation: {
      date: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        },
      },
      flight_from: { required },
      flight_to: { required },
      airline: { required },
      departure: { required },
      arrival: {
        required: requiredIf(function () {
          return !this.form_flight_reservation.already_booked
        }),
      },
      sales_status: { required },
    },
    form_safari_reservation: {
      cars_required: { required },
      pax: { required },
      start_from: { required },
      finish_at: { required },
      group_type: { required },
    },
    form_request_meal: {
      hotel: { required },
      type: { required },
      date: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        },
      },
      time: { required, timeRegex },
      clients: { required },
      crew: { required },
    },
  },
  computed: {
    safari_itinerary_error() { return safariItineraryError(this.itinerary_safari_list) },
    canOpenFlightBooking() {
      return this.$store.state.userGroups.some(role => flightBookingRoles.includes(role.code))
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    calcNumberOfNights() {
      if (this.form_hotel_reservation.check_in && this.form_hotel_reservation.check_out) {
        const date1 = new Date(this.form_hotel_reservation.check_in)
        const date2 = new Date(this.form_hotel_reservation.check_out)
        const timeDiff = date2.getTime() - date1.getTime()
        return Math.ceil(timeDiff / (1000 * 3600 * 24)) < 0 ? 0 : Math.ceil(timeDiff / (1000 * 3600 * 24))
      } else {
        return ''
      }
    }
  },
  created() {
    this.fetchAllLists()
    getHotelsListSearch({ active: true }).then(res => { this.hotels_options = res.data })
    getReservationsSalesStatuses().then(res => { this.reservations_sales_statuses = res.data })
    getReservationsMealType().then(res => { this.meal_type_options = res.data })
    getLanguages().then(res => { this.language_options = res.data })
  },
  beforeDestroy() {
    this.flight_bookings_request_id++
  },
  watch: {
    '$route.params.id'() {
      this.fetchFlightBookings()
    },
  },
  methods: {
    async fetchFlightBookings() {
      const requestId = ++this.flight_bookings_request_id
      this.flight_bookings = []
      this.flight_bookings_loading = true
      this.flight_bookings_error = false
      const params = {
        lead: +this.$route.params.id,
        page_size: 50,
        ...(this.active_filter === 'active' ? { status_exclude: 'cancelled' } : { status: 'cancelled' }),
      }
      try {
        const { data } = await getFlightsBookings(params)
        if (requestId !== this.flight_bookings_request_id) return
        this.flight_bookings = data.results || data
      } catch (error) {
        if (requestId === this.flight_bookings_request_id) this.flight_bookings_error = true
      } finally {
        if (requestId === this.flight_bookings_request_id) this.flight_bookings_loading = false
      }
    },
    onOpenAviasales() {
      this.aviasalesInitialSearch = leadFlightSearch(this.leadInfo, format(new Date(), 'yyyy-MM-dd'))
      this.aviasalesOpen = true
      this.$modal.show('lead-aviasales-modal')
    },
    onClickImportSafari() {
      this.$refs.import_safari.preload = true
      postReservationsSafariStoryImport({ program_id: this.leadInfo.itinerary.itinerary_id })
        .finally(() => { this.$refs.import_safari.preload = false })
        .then((res) => { this.itinerary_safari_list = res.data.itineraries })
    },
    onItineraryTypeSelected() {
      if (this.itinerary_safari_config.type.code === 'rest_day') {
        this.itinerary_safari_config.national_park = null
        this.itinerary_safari_config.national_park_area = null
      }
    },
    onDepartureScheduleChange() {
      this.form_flight_reservation.arrival = this.form_flight_reservation.departure.arrival
    },
    sendRequestMealForm() {
      this.$refs.request_meal_submit.preload = true
      postReservationsMeal(
        {
          lead: +this.$route.params.id,
          hotel: this.form_request_meal.hotel,
          already_booked: this.form_request_meal.already_booked,
          requests: this.form_request_meal.requests
            .map(item => (
              { ...item, type: item.type.code }
            ))
        }
      )
        .finally(() => { this.$refs.request_meal_submit.preload = false })
        .then(() => {
          this.$modal.hide('request-meal-popup')
          this.$notify({
            type: 'success',
            title: 'Meal created',
          })
          this.fetchMealsItems()
        })
    },
    onDeleteMealsCard(index) {
      this.form_request_meal.requests.splice(index, 1)
    },
    onClickRequestMealConfig() {
      this.$v.form_request_meal.$touch()
      if (this.$v.form_request_meal.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        const { requests, ...rest } = this.form_request_meal
        this.form_request_meal.requests.push({ ...rest })
        this.form_request_meal.type = null
        this.form_request_meal.time = null
        this.form_request_meal.clients = null
        this.form_request_meal.crew = null
        this.$v.form_request_meal.$reset()
      }
    },
    onClickBookMealButton() {
      this.form_request_meal = { requests: [] }
      this.$v.form_request_meal.$reset()
      this.$modal.show('request-meal-popup')
    },
    handleInputChange(changedField) {
      const fields = ['flight_from', 'flight_to', 'airline', 'departure']
      const changedFieldIndex = fields.indexOf(changedField)
      if (changedFieldIndex < fields.length - 1) {
        for (let i = changedFieldIndex + 1; i < fields.length; i++) {
          this.form_flight_reservation[fields[i]] = null
        }
      }
    },
    onCheckCreateSafariForm() {
      this.$v.form_safari_reservation.$touch()
      if (this.$v.form_safari_reservation.$invalid || this.safari_itinerary_error) {
        this.$notify({
          type: 'info',
          title: this.safari_itinerary_error || 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendCreateSafariForm()
      }
    },
    sendCreateSafariForm() {
      this.$refs.submit_safari_create.preload = true
      postReservationsSafari(this.normalizeCreateSafariForm())
        .finally(() => { this.$refs.submit_safari_create.preload = false })
        .then(() => {
          this.fetchSafariItems()
          this.$modal.hide('safari-reservation-create-modal')
          this.$notify({
            type: 'success',
            title: 'Reservation created',
          })
        })
    },
    normalizeCreateSafariForm() {
      const normalizedForm = { ...this.form_safari_reservation }

      normalizedForm.lead = +this.$route.params.id
      if (normalizedForm.group_type) normalizedForm.type = normalizedForm.group_type
      if (normalizedForm.start_from) normalizedForm.start_from = normalizedForm.start_from.id
      if (normalizedForm.finish_at) normalizedForm.finish_at = normalizedForm.finish_at.id
      if (this.itinerary_safari_list && this.itinerary_safari_list.length > 0) {
        normalizedForm.itineraries = this.itinerary_safari_list.map(item => (
          {
            date: item.date,
            type: item.type.code,
            national_park: item.national_park?.code || null,
            national_park_area: item.national_park_area?.id || null,
          }
        ))
      }
      delete normalizedForm.group_type

      return normalizedForm
    },
    onDeleteCreateSafariItinerary(index) {
      this.itinerary_safari_list.splice(index, 1)
    },
    onAddItinerary() {
      this.itinerary_safari_list.push({ ...this.itinerary_safari_config })
      this.itinerary_safari_list.sort((a, b) => new Date(a.date) - new Date(b.date))
    },
    onSelectMissingSafariDay(date) {
      this.$set(this.itinerary_safari_config, 'date', date)
      this.$notify({ type: 'info', title: 'Date selected. Choose a program type above, then click Add.' })
    },
    onNationalParkSelected() {
      delete this.itinerary_safari_config.national_park_area
      this.area_options = this.itinerary_safari_config.national_park.area
    },
    onLocationStartSearch(search, loading) {
      loading(true)
      this.performLocationSearch(search, loading, 'location_start_options', this)
    },
    onLocationFinishSearch(search, loading) {
      loading(true)
      this.performLocationSearch(search, loading, 'location_finish_options', this)
    },
    performLocationSearch: debounce((search, loading, fieldKey, vm) => {
      if (!search) {
        vm[fieldKey] = []
        loading(false)
      } else {
        getGoogleSearch(search).then(response => {
          vm[fieldKey] = response.data
          loading(false)
        })
      }
    }, 350),
    onLocationStartSelected({ name, place_id }, form) {
      postLocation({ name, place_id }).then(res => {
        this[form].start_from = res.data
      })
    },
    onLocationFinishSelected({ name, place_id }, form) {
      postLocation({ name, place_id }).then(res => {
        this[form].finish_at = res.data
      })
    },
    onClickBookSafariButton() {
      this.$refs.book_safari.preload = true
      this.form_safari_reservation = {
        pax: this.leadInfo.pax,
        spoken_language: null,
        honeymooners: false,
        birdwatchers: false,
        special_assistance: false,
      }
      this.itinerary_safari_config = { type: {} }
      this.itinerary_safari_list = []
      this.$v.form_safari_reservation.$reset()
      Promise.all([
        getSafariGroupTypes({ reservations: true }),
        getReservationsItineraryTypes(),
        getSafariNationalPark({ reservations: true })
      ])
        .finally(() => { this.$refs.book_safari.preload = false })
        .then(res => {
          this.group_type_options = res[0].data
          this.itinerary_types_options = res[1].data
          this.itinerary_safari_config.type = res[1].data[0]
          this.national_park_options = res[2].data
          this.$modal.show('safari-reservation-create-modal')
        })
    },
    onCheckCreateFlightForm() {
      this.$v.form_flight_reservation.$touch()
      if (this.$v.form_flight_reservation.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendCreateFlightForm()
      }
    },
    sendCreateFlightForm() {
      this.$refs.submit_flight_create.preload = true
      postReservationsFlights(this.normalizeCreateFlightForm())
        .finally(() => { this.$refs.submit_flight_create.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Reservation created',
          })
          this.fetchFlightsItems()
          this.$modal.hide('flight-reservation-create-modal')
          this.seats_options = []
        })
    },
    normalizeCreateFlightForm() {
      const {
        sales_status,
        departure,
        date,
        notes,
        airline,
        flight_from,
        flight_to,
        already_booked,
        arrival
      } = this.form_flight_reservation
      const { id: lead } = this.$route.params

      return {
        sales_status,
        lead,
        date,
        notes,
        airline: airline.id,
        origin: flight_from,
        destination: flight_to,
        departure: this.switcher_flight_search ? departure.departure : departure,
        arrival,
        seats_short: this.seats_options.map(item => (
          { luggage_option: item.id, count: item.count }
        )),
        already_booked
      }
    },
    onLuggageClick(luggage) {
      this.seats_options.push({ ...luggage, count: 1 })
    },
    onAirlineChange() {
      this.seats_options = []
      this.handleInputChange('airline')

      getDomesticFlightsSchedulesScheduleSelection({
        date: this.form_flight_reservation.date,
        flight_from: this.form_flight_reservation.flight_from,
        flight_to: this.form_flight_reservation.flight_to,
        airline: this.form_flight_reservation.airline.id,
      })
        .then(res => { this.schedule_options = res.data })
    },
    onOriginChange() {
      this.handleInputChange('flight_from')
      getDomesticFlightsSchedulesDestinationAvailable({
        date: this.form_flight_reservation.date,
        flight_from: this.form_flight_reservation.flight_from
      })
        .then(res => { this.flight_to_options = res.data })
    },
    onDestinationChange() {
      this.handleInputChange('flight_to')
      getDomesticFlightsSchedulesAirlinesAvailable({
        date: this.form_flight_reservation.date,
        flight_from: this.form_flight_reservation.flight_from,
        flight_to: this.form_flight_reservation.flight_to,
      })
        .then(res => { this.airline_options = res.data })
    },
    onDateChanged() {
      delete this.form_flight_reservation.flight_from
      delete this.form_flight_reservation.flight_to
      delete this.form_flight_reservation.airline
      getDomesticFlightsSchedulesOriginAvailable({ date: this.form_flight_reservation.date })
        .then(res => { this.flight_from_options = res.data })
    },
    onClickBookFlightButton() {
      getReservationsSalesStatuses({ flights_create: true })
        .then(res => { this.reservations_sales_statuses_flights = res.data })
      getDomesticFlightsAirlines().then(res => { this.airline_alt_options = res.data })
      this.form_flight_reservation = {
        date: null,
        sales_status: null,
        flight_from: null,
        flight_to: null,
        airline: null,
        departure: null,
        arrival: null,
      }
      this.$v.form_flight_reservation.$reset()
      this.$modal.show('flight-reservation-create-modal')
    },
    onCheckImportHotelForm() {
      this.$v.imported_hotels.$touch()
      if (this.$v.imported_hotels.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendImportHotelForm()
      }
    },
    sendImportHotelForm() {
      this.$refs.send_import_hotels.preload = true
      postReservationsHotelsStoryCreate(this.normalizeImportHotelForm())
        .finally(() => { this.$refs.send_import_hotels.preload = false })
        .then(() => {
          this.fetchHotelsItems()
          this.$modal.hide('import-hotels-modal')
        })
    },
    normalizeImportHotelForm() {
      const leadId = +this.$route.params.id

      const reservations = this.imported_hotels
        .filter(item => item.checked_import)
        .map(({ rooms, hotel, status, notes }) => ({
          rooms: rooms.map(({
            hotel_room,
            accomodation_type,
            early_check_in_time,
            late_check_out_time,
            check_in,
            check_out,
            meal_plan,
            count,
            bed_in_twin_action,
            bed_in_twin_room_id
          }) => {
            const base = {
              hotel_room: hotel_room.id,
              accomodation_type: accomodation_type.code,
              early_check_in_time,
              late_check_out_time,
              check_in,
              check_out,
              meal_plan: meal_plan.code,
              count
            }

            return hotel_room?.bed_in_twin && hotel.property ? { ...base, bed_in_twin_room_id, bed_in_twin_action } : base
          }),
          hotel: hotel.id,
          sales_status: !hotel.property ? status : 'book',
          notes
        }))

      return { lead: leadId, reservations }
    },
    onClickImportHotelsButton() {
      this.$refs.import_hotels.preload = true
      postReservationsHotelsStoryImport({ program_id: this.leadInfo.itinerary.itinerary_id })
        .finally(() => { this.$refs.import_hotels.preload = false })
        .then(res => {
          this.imported_hotels = res.data
          if (this.is_general_manager) {
            this.imported_hotels = this.imported_hotels.map(hotel => (
              { ...hotel, already_booked: true }
            ))
          }
          this.$modal.show('import-hotels-modal')
        })
    },
    onCheckCreateHotelForm() {
      this.$v.form_hotel_reservation.$touch()
      if (this.$v.form_hotel_reservation.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendCreateHotelForm()
      }
    },
    sendCreateHotelForm() {
      this.$refs.submit_hotel_create.preload = true
      postReservationsHotelsCreate(this.normalizeCreateHotelForm())
        .then(() => {
          this.fetchHotelsItems()
          this.$modal.hide('hotel-reservation-create-modal')
          this.$notify({
            type: 'success',
            title: 'Reservation created',
          })
        })
        .catch(() => { this.$refs.submit_hotel_create.preload = false })
    },
    normalizeCreateHotelForm() {
      const form = {}

      form.already_booked = !this.form_hotel_reservation.hotel.property ? this.form_hotel_reservation.already_booked : false
      form.sales_status = !this.form_hotel_reservation.hotel.property ? this.form_hotel_reservation.sales_status : 'book'
      form.hotel = this.form_hotel_reservation.hotel.id
      form.lead = +this.$route.params.id
      form.notes = this.form_hotel_reservation.notes

      form.rooms_short = this.hotel_short_rooms_checked.map(({
        type_of_rooms,
        accomodation_type,
        meal_plan,
        bed_in_twin_action,
        bed_in_twin_room_id,
        check_in,
        check_out,
        count,
        early_check_in_time,
        late_check_out_time,
      }) => {
        const base = {
          accomodation_type: accomodation_type?.code ?? accomodation_type,
          hotel_room: type_of_rooms.id,
          meal_plan: meal_plan?.code ?? meal_plan,
          check_in,
          check_out,
          count,
          early_check_in_time,
          late_check_out_time,
        }

        return type_of_rooms?.bed_in_twin && this.form_hotel_reservation.hotel.property ? { ...base, bed_in_twin_room_id, bed_in_twin_action } : base
      })

      return form
    },
    onShortRoomDelete(index, targetObj) {
      this[targetObj].splice(index, 1)
    },
    onCountChange(count, index, targetObj) {
      this[targetObj][index].count = +count
    },
    onChangeSomeField({ value, field }, index, targetObj) {
      this[targetObj][index][field] = value
    },
    onChangeSomeFieldImport({ value, field }, room_index, index) {
      this.imported_hotels[index].rooms[room_index][field] = value
    },
    onChangeSwitcherFlightSearch(val) {
      this.form_flight_reservation = {
        date: this.form_flight_reservation.date,
        sales_status: null,
        flight_from: null,
        flight_to: null,
        airline: null,
        departure: null,
        arrival: null,
      }
      this.seats_options = []
      this.$v.form_flight_reservation.$reset()
    },
    onRoomIconClick(type) {
      const room = this.convertToRoom(type)

      if (!this.checkExistsInArray(this.hotel_short_rooms_checked, room)) {
        this.hotel_short_rooms_checked.push(room)
        this.sortArrayByDate('hotel_short_rooms_checked')
      }
    },
    convertToRoom(type) {
      return {
        accomodation_type: type,
        type_of_rooms: this.form_hotel_reservation.type_of_rooms,
        check_in: this.form_hotel_reservation.check_in,
        check_out: this.form_hotel_reservation.check_out,
        count: 1,
        early_check_in_time: this.form_hotel_reservation.early_check_in ? this.form_hotel_reservation.early_check_in_time : null,
        late_check_out_time: this.form_hotel_reservation.late_check_out ? this.form_hotel_reservation.late_check_out_time : null,
        meal_plan: this.form_hotel_reservation.meal_plan
      }
    },
    checkExistsInArray(array, obj) {
      if (array) {
        return array.some(element => {
          const { count, ...restElement } = element
          const { count: objCount, ...restObj } = obj

          return JSON.stringify(restElement) === JSON.stringify(restObj)
        })
      }
    },
    sortArrayByDate(array) {
      this[array] = this[array].sort((a, b) => new Date(a.check_in) - new Date(b.check_in))
    },
    onChangeSelectHotelReservation() {
      this.meal_plans_options = this.form_hotel_reservation.hotel.meal_plans
      this.form_hotel_reservation.early_check_in_time = this.form_hotel_reservation.hotel.check_in
      this.form_hotel_reservation.late_check_out_time = this.form_hotel_reservation.hotel.check_out
      this.hotel_short_rooms_checked = []
      delete this.form_hotel_reservation.type_of_rooms
      delete this.form_hotel_reservation.meal_plan
      getHotelsRooms(this.form_hotel_reservation.hotel.id).then(res => { this.rooms_options = res.data })
    },
    onClickBookHotelButton() {
      this.form_hotel_reservation = {}
      this.hotel_short_rooms_checked = []
      this.$v.form_hotel_reservation.$reset()
      this.$modal.show('hotel-reservation-create-modal')
    },
    fetchHotelsItems() {
      getReservationsHotelsList(this.getFilter(this.active_filter === 'active'))
        .then(res => { this.hotels_reservations = res.data })
    },
    fetchFlightsItems() {
      getReservationsFlights(this.getFilter(this.active_filter === 'active'))
        .then(res => { this.flights_reservations = res.data })
    },
    fetchSafariItems() {
      getReservationsSafari(this.getFilter(this.active_filter === 'active'))
        .then(res => { this.safari_reservations = res.data })
    },
    fetchMealsItems() {
      getReservationsMeals(this.getFilter(this.active_filter === 'active'))
        .then(res => { this.meals_reservations = res.data })
    },
    getFilter(isActive, additionalFilters = {}) {
      const leadId = this.$route.params.id
      const baseFilter = isActive ? {
        lead: leadId,
        reservation_status_exclude: 'cancelled',
        sales_status_exclude: 'cancel',
      } : {
        lead: leadId,
        sales_status: 'cancel'
      }

      return { ...baseFilter, ...additionalFilters }
    },
    fetchAllLists() {
      this.fetchFlightBookings()
      this.is_loaded = false
      Promise.all([
        getReservationsHotelsList(this.getFilter(this.active_filter === 'active')),
        getReservationsFlights(this.getFilter(this.active_filter === 'active')),
        getReservationsSafari(this.getFilter(this.active_filter === 'active')),
        getReservationsMeals(this.getFilter(this.active_filter === 'active'))
      ])
        .then(res => {
          this.hotels_reservations = res[0].data
          this.flights_reservations = res[1].data
          this.safari_reservations = res[2].data
          this.meals_reservations = res[3].data
          this.is_loaded = true
        })
    },
    onChangeActiveFilter() {
      this.fetchAllLists()
    },
  }
}
</script>

<style lang="sass" scoped>
.lead-aviasales-popup
  min-height: 400px

.import-hotel
  padding: 30px 0
  border-bottom: 1px solid #ebebeb

  &:last-child
    border: none
</style>
